import { type ButtonColor } from "@/components/interactive/Button";
import { ButtonV2, type ButtonV2Variant } from "@/components/interactive/ButtonV2";
import { InputV2 } from "@/components/interactive/InputV2";
import { ModalCard, ModalHeading, SimpleModal, SimpleModalV2 } from "@/components/modal/Modal";
import { classNames } from "@/utils/classnames";
import { useKeyDown } from "@/utils/hooks/use-key-down";
import { ReactNode, useCallback, useState } from "react";
import { create as createModal } from "@/components/modal/promise-modal";

type ConfirmModalPresentation = "legacy" | "v2";

/** A simple template for a modal for confirming a delete operation. */
export function createConfirmModal({
  title = "Confirm Delete",
  body = "Are you sure you want to delete this item? This operation cannot be undone.",
  removeLabel = "Delete",
  buttonColor = "danger",
  size = "sm",
  cancelButtonColor = "secondary",
  cancelLabel = "Cancel",
  modalClassName,
  presentation = "legacy",
  confirmationPhrase,
  confirmTestId,
}: {
  title?: ReactNode;
  body?: ReactNode;
  removeLabel?: string;
  cancelLabel?: string;
  buttonColor?: ButtonColor;
  cancelButtonColor?: ButtonColor;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
  modalClassName?: string;
  presentation?: ConfirmModalPresentation;
  confirmationPhrase?: string;
  confirmTestId?: string;
} = {}) {
  return createModal(({ isOpen, onResolve }) => {
    return (
      <ConfirmModalContent
        isOpen={isOpen}
        onResolve={onResolve}
        title={title}
        body={body}
        removeLabel={removeLabel}
        buttonColor={buttonColor}
        size={size}
        cancelButtonColor={cancelButtonColor}
        cancelLabel={cancelLabel}
        modalClassName={modalClassName}
        presentation={presentation}
        confirmationPhrase={confirmationPhrase}
        confirmTestId={confirmTestId}
      />
    );
  })();
}

const buttonColorToV2Variant = (color: ButtonColor): ButtonV2Variant => {
  switch (color) {
    case "danger":
    case "danger-subdued":
      return "destructive";
    case "primary":
    case "accent-lime":
      return "primary";
    case "secondary":
    case "warning":
    case "white":
      return "secondary";
  }
};

const buttonLabelToString = (label: ReactNode, fallback: string): string => {
  if (typeof label === "string") {
    return label;
  }

  if (typeof label === "number") {
    return String(label);
  }

  return fallback;
};

const ConfirmModalContent: React.FC<{
  isOpen: boolean;
  onResolve: (value: boolean) => void;
  title: ReactNode;
  body: ReactNode;
  removeLabel: string;
  buttonColor: ButtonColor;
  size: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
  cancelButtonColor: ButtonColor;
  cancelLabel: string;
  modalClassName?: string;
  presentation: ConfirmModalPresentation;
  confirmationPhrase?: string;
  confirmTestId?: string;
}> = ({
  isOpen,
  onResolve,
  title,
  body,
  removeLabel,
  buttonColor,
  size,
  cancelButtonColor,
  cancelLabel,
  modalClassName,
  presentation,
  confirmationPhrase,
  confirmTestId,
}) => {
  const [confirmation, setConfirmation] = useState("");
  const confirmationMatches = confirmationPhrase == null || confirmation === confirmationPhrase;
  const confirm = useCallback(() => {
    if (confirmationMatches) {
      onResolve(true);
    }
  }, [confirmationMatches, onResolve]);
  useKeyDown(confirm, { key: "Enter" });

  const confirmationInput =
    confirmationPhrase == null ? null : (
      <InputV2
        autoComplete="off"
        label={`Type “${confirmationPhrase}” to confirm`}
        description="The phrase must match exactly. Pasting is disabled."
        spellCheck={false}
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        onPaste={(event) => event.preventDefault()}
      />
    );

  if (presentation === "v2") {
    return (
      <SimpleModalV2
        isOpen={isOpen}
        onClose={() => onResolve(false)}
        modalClassName={modalClassName}
      >
        <ModalCard
          size={size}
          surfaceClassName={"bg-cds-surface-default dark:bg-cds-inset-dark"}
          className={classNames(
            "rounded-cds-8 border-[1px] border-cds-surface-border !p-cds-16 shadow-xl dark:border-white/[0.08]",
            size === "sm"
              ? "!w-[360px] !max-w-[calc(100vw-64px)]"
              : "!w-[calc(100vw-64px)] sm:!w-full",
          )}
        >
          <div className={"space-y-cds-12"}>
            <div className={"space-y-cds-6"}>
              <h2
                className={
                  "cursor-default text-cds-md font-medium text-cds-ink-strong dark:text-zinc-100"
                }
              >
                {title}
              </h2>
              <div
                className={
                  "whitespace-normal break-words text-cds-sm text-cds-ink-subtle dark:text-zinc-400"
                }
              >
                {body}
              </div>
              {confirmationInput}
            </div>
            <div className={"flex justify-end gap-cds-8"}>
              <ButtonV2
                type={"button"}
                size={"sm"}
                variant={buttonColorToV2Variant(cancelButtonColor)}
                onClick={() => onResolve(false)}
              >
                {buttonLabelToString(cancelLabel, "Cancel")}
              </ButtonV2>
              <ButtonV2
                data-testid={confirmTestId}
                type={"button"}
                size={"sm"}
                variant={buttonColorToV2Variant(buttonColor)}
                disabled={!confirmationMatches}
                onClick={confirm}
              >
                {buttonLabelToString(removeLabel, "Confirm")}
              </ButtonV2>
            </div>
          </div>
        </ModalCard>
      </SimpleModalV2>
    );
  }

  return (
    <SimpleModal isOpen={isOpen} onClose={() => onResolve(false)} modalClassName={modalClassName}>
      <ModalCard className="space-y-cds-12" size={size}>
        <ModalHeading>{title}</ModalHeading>
        <div className="whitespace-normal break-words text-cds-sm text-cds-ink-subtle dark:text-zinc-400">
          {body}
        </div>
        {confirmationInput}
        <div className="flex justify-end gap-cds-8">
          <ButtonV2
            type={"button"}
            size={"sm"}
            variant={buttonColorToV2Variant(cancelButtonColor)}
            onClick={() => onResolve(false)}
          >
            {cancelLabel}
          </ButtonV2>
          <ButtonV2
            data-testid={confirmTestId}
            type={"button"}
            size={"sm"}
            variant={buttonColorToV2Variant(buttonColor)}
            disabled={!confirmationMatches}
            onClick={confirm}
          >
            {removeLabel}
          </ButtonV2>
        </div>
      </ModalCard>
    </SimpleModal>
  );
};
